import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { InstanceLabel } from '../api/client'
import { useLabels, useCreateLabel, useUpdateLabel, useDeleteLabel } from '../hooks/useLabels'
import { ConfirmDialog, LinkButton } from './ui'

const PALETTE = ['#6366f1', '#8b5cf6', '#ec4899', '#ef4444', '#f97316', '#eab308', '#22c55e', '#14b8a6', '#0ea5e9', '#64748b']

function ColorSwatches({ value, onChange }: { value: string; onChange: (c: string) => void }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {PALETTE.map((c) => (
        <button
          key={c}
          type="button"
          onClick={() => onChange(c)}
          className={`h-6 w-6 rounded-full transition-transform ${value === c ? 'ring-2 ring-offset-1 ring-gray-400 scale-110' : 'hover:scale-110'}`}
          style={{ backgroundColor: c }}
        />
      ))}
    </div>
  )
}

function LabelRow({ label }: { label: InstanceLabel }) {
  const { t } = useTranslation()
  const update = useUpdateLabel()
  const del = useDeleteLabel()
  const [editing, setEditing] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [name, setName] = useState(label.name)
  const [color, setColor] = useState(label.color)

  if (editing) {
    return (
      <div className="rounded-lg border border-gray-200 p-3 space-y-2">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={40}
          className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
        />
        <ColorSwatches value={color} onChange={setColor} />
        <div className="flex items-center gap-2">
          <button
            onClick={() => { if (name.trim()) update.mutate({ id: label.id, name: name.trim(), color }, { onSuccess: () => setEditing(false) }) }}
            disabled={update.isPending || !name.trim()}
            className="rounded-md bg-brand-600 px-3 py-1 text-xs font-medium text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {t('dashboard.labels.save')}
          </button>
          <button onClick={() => { setEditing(false); setName(label.name); setColor(label.color) }} className="text-xs text-gray-500 hover:text-gray-700">
            {t('dashboard.labels.cancel')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2">
      <span className="h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: label.color }} />
      <span className="flex-1 truncate text-sm text-gray-800">{label.name}</span>
      <LinkButton tone="neutral" onClick={() => setEditing(true)} className="text-gray-400 hover:text-brand-600">
        {t('dashboard.labels.edit', 'Edit')}
      </LinkButton>
      <LinkButton
        tone="danger"
        onClick={() => setConfirming(true)}
        disabled={del.isPending}
        className="text-gray-400 hover:text-red-600"
      >
        {t('dashboard.labels.delete')}
      </LinkButton>

      {confirming && (
        <ConfirmDialog
          title={t('dashboard.labels.delete')}
          message={t('dashboard.labels.deleteConfirm')}
          confirmLabel={t('dashboard.labels.delete')}
          busy={del.isPending}
          onConfirm={() => del.mutate(label.id, { onSuccess: () => setConfirming(false) })}
          onCancel={() => setConfirming(false)}
        />
      )}
    </div>
  )
}

export default function LabelManager({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation()
  const { data: labels } = useLabels()
  const create = useCreateLabel()
  const [name, setName] = useState('')
  const [color, setColor] = useState(PALETTE[0])

  const add = () => {
    if (!name.trim()) return
    create.mutate({ name: name.trim(), color }, { onSuccess: () => { setName(''); setColor(PALETTE[0]) } })
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3.5">
          <h2 className="text-base font-semibold text-gray-900">{t('dashboard.labels.title')}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label={t('dashboard.labels.cancel')}>
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="max-h-[60vh] space-y-4 overflow-y-auto p-5">
          <div className="rounded-lg border border-dashed border-gray-300 p-3 space-y-2">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') add() }}
              maxLength={40}
              placeholder={t('dashboard.labels.namePlaceholder')}
              className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
            <ColorSwatches value={color} onChange={setColor} />
            <button
              onClick={add}
              disabled={create.isPending || !name.trim()}
              className="rounded-md bg-brand-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {t('dashboard.labels.add')}
            </button>
          </div>

          <div className="space-y-2">
            {(labels ?? []).length === 0 ? (
              <p className="py-4 text-center text-sm text-gray-400">{t('dashboard.labels.empty')}</p>
            ) : (
              (labels ?? []).map((l) => <LabelRow key={l.id} label={l} />)
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
